// src/features/quality/pages/QualityCheckRevisionCreatePage.tsx
//
// ADR-091 §3: this page is USELESS without AM-007 merged — it calls
// POST /quality-check-revisions/, which 405'd before that patch.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// `npm run api:generate` is re-run against the AM-007-patched schema.yml.
import { useV1QualityCheckRevisionsCreate } from "@/api/generated/v1/v1";
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { setCustomFieldValues } from "@/features/customFields/api";
import { PageBanner } from "@/shared/components/PageBanner";

export function QualityCheckRevisionCreatePage() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});
  const [customFieldsValid, setCustomFieldsValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useV1QualityCheckRevisionsCreate();

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) {
      setError("نام Revision الزامی است.");
      return;
    }
    if (!customFieldsValid) {
      setError("لطفاً فیلدهای سفارشی الزامی را پر کنید.");
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        data: {
          family: familyId,
          name,
          description,
          is_active: isActive,
        } as any,
      });
      // apps/quality/api/views.py::QualityCheckRevisionViewSet.create
      // (AM-007). No client-side guessing of these values.
      
      if (Object.keys(customFieldValues).length > 0) {
        await setCustomFieldValues("quality.qualitycheckrevision", result.id, customFieldValues);
      }

      navigate(`/quality/revisions/${result.id}`);
    } catch (err: any) {
      const backendError =
        err?.response?.data?.family?.[0] ??
        err?.response?.data?.name?.[0] ??
        err?.response?.data?.non_field_errors?.[0] ??
        "خطا در ایجاد Revision.";
      setError(backendError);
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="تجدیدنظر (Revision) جدید"
        subtitle="ایجاد یک ساختار جدید برای ارزیابی کیفی"
        icon="🛡️"
        helpLink="/help/quality/quality-check-revision-create"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← انصراف
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            style={{
              padding: "0.6rem 1.25rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              fontWeight: 700,
              fontSize: "0.9rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>💾</span>
            <span>ثبت تجدیدنظر</span>
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
          <div className="form-input-control">
            <label htmlFor="revName">نام تجدیدنظر</label>
            <input
              id="revName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="مثال: Revision A"
            />
          </div>

          <div className="form-input-control">
            <label htmlFor="revDesc">توضیحات</label>
            <textarea
              id="revDesc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="توضیحات مربوط به تغییرات این تجدیدنظر..."
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              id="isActiveCheck"
              style={{ width: "1.2rem", height: "1.2rem", cursor: "pointer" }}
            />
            <label htmlFor="isActiveCheck" style={{ cursor: "pointer", fontWeight: 600 }}>فعال باشد</label>
          </div>
        </div>

        <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--color-border)", paddingTop: "1.5rem" }}>
          <CustomFieldsPanel
            targetContentType="quality.qualitycheckrevision"
            hideSaveButton={true}
            onChange={(values, isValid) => {
              setCustomFieldValuesState(values);
              setCustomFieldsValid(isValid);
            }}
          />
        </div>

        {error && (
          <div style={{ marginTop: "1rem", padding: "1rem", background: "rgba(239,68,68,0.1)", color: "#EF4444", borderRadius: "8px", fontWeight: 600 }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
